import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import escapeStringRegexp from 'escape-string-regexp';
import { extension, command, keyBinding, NamedShortcut, PlainExtension, isString, isSelectionEmpty, cx, isNumber, assertGet, isEmptyArray, getSelectedWord, findMatches } from '@remirror/core';
import { DecorationSet, Decoration } from '@remirror/pm/view';

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _dec10, _class, _class2;
var SearchExtension = (_dec = extension({
  defaultOptions: {
    autoSelectNext: true,
    searchClass: 'search',
    highlightedClass: 'highlighted-search',
    searching: false,
    caseSensitive: false,
    disableRegex: true,
    alwaysSearch: false,
    clearOnEscape: true
  },
  handlerKeys: ['onSearch'],
  staticKeys: ['searchClass', 'highlightedClass']
}), _dec2 = command(), _dec3 = command(), _dec4 = command(), _dec5 = command(), _dec6 = command(), _dec7 = command(), _dec8 = keyBinding({
  shortcut: NamedShortcut.Find
}), _dec9 = keyBinding({
  shortcut: NamedShortcut.FindBackwards
}), _dec10 = keyBinding({
  shortcut: 'Escape',
  isActive: options => options.clearOnEscape
}), _dec(_class = (_class2 = class SearchExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "_updating", false);

    _defineProperty(this, "_results", []);

    _defineProperty(this, "_activeIndex", 0);
  }

  get name() {
    return 'search';
  }

  /**
   * This plugin is responsible for adding something decorations to the
   */
  createPlugin() {
    return {
      state: {
        init() {
          return DecorationSet.empty;
        },

        apply: (tr, old) => {
          if (this._updating || this.options.searching || tr.docChanged && this.options.alwaysSearch) {
            return this.createDecoration(tr.doc);
          }

          if (tr.docChanged) {
            return old.map(tr.mapping, tr.doc);
          }

          return old;
        }
      },
      props: {
        decorations: state => {
          return this.getPluginState(state);
        }
      }
    };
  }
  /**
   * Find a search term in the editor. If no search term is provided it
   * defaults to the currently selected text.
   */


  search(searchTerm, direction) {
    return this.find(searchTerm, direction);
  }
  /**
   * Find the next occurrence of the search term.
   */


  searchNext() {
    return this.find(this._searchTerm, 'next');
  }
  /**
   * Find the previous occurrence of the search term.
   */


  searchPrevious() {
    return this.find(this._searchTerm, 'previous');
  }
  /**
   * Replace the provided
   */


  replaceSearchResult(replacement, index) {
    return this.replace(replacement, index);
  }
  /**
   * Replaces all search results with the replacement text.
   */


  replaceAllSearchResults(replacement) {
    return this.replaceAll(replacement);
  }
  /**
   * Clears the current search.
   */


  clearSearch() {
    return this.clear();
  }

  searchForwardShortcut(props) {
    return this.createSearchKeyBinding('next')(props);
  }

  searchBackwardShortcut(props) {
    return this.createSearchKeyBinding('previous')(props);
  }

  escapeShortcut(_) {
    if (!isString(this._searchTerm)) {
      return false;
    }

    this.clearSearch();
    return true;
  }

  createSearchKeyBinding(direction) {
    return _ref => {
      var state = _ref.state;
      var searchTerm;

      if (isSelectionEmpty(state)) {
        if (!this._searchTerm) {
          return false;
        }

        searchTerm = this._searchTerm;
      }

      this.find(searchTerm, direction);
      return true;
    };
  }

  findRegExp() {
    var _this$_searchTerm;

    return new RegExp((_this$_searchTerm = this._searchTerm) !== null && _this$_searchTerm !== void 0 ? _this$_searchTerm : '', !this.options.caseSensitive ? 'gui' : 'gu');
  }

  getDecorations() {
    return this._results.map((deco, index) => Decoration.inline(deco.from, deco.to, {
      class: cx(this.options.searchClass, index === this._activeIndex && this.options.highlightedClass)
    }));
  }

  gatherSearchResults(doc) {
    var _this = this;

    this._results = [];
    var mergedTextNodes = [];
    var index = 0;

    if (!this._searchTerm) {
      return;
    }

    doc.descendants((node, pos) => {
      var _node$text2;

      var mergedTextNode = mergedTextNodes[index];

      if (!node.isText && mergedTextNode) {
        index += 1;
        return;
      }

      if (mergedTextNode) {
        var _node$text;

        mergedTextNodes[index] = {
          text: "".concat(mergedTextNode.text).concat((_node$text = node.text) !== null && _node$text !== void 0 ? _node$text : ''),
          pos: mergedTextNode.pos + (index === 0 ? 1 : 0)
        };
        return;
      }

      mergedTextNodes[index] = {
        text: (_node$text2 = node.text) !== null && _node$text2 !== void 0 ? _node$text2 : '',
        pos
      };
    });

    var _loop = function _loop() {
      var _mergedTextNodes$_i = _mergedTextNodes[_i],
          text = _mergedTextNodes$_i.text,
          pos = _mergedTextNodes$_i.pos;

      var search = _this.findRegExp();

      findMatches(text, search).forEach(match => {
        _this._results.push({
          from: pos + match.index,
          to: pos + match.index + assertGet(match, 0).length
        });
      });
    };

    for (var _i = 0, _mergedTextNodes = mergedTextNodes; _i < _mergedTextNodes.length; _i++) {
      _loop();
    }
  }

  replace(replacement, index) {
    return props => {
      var tr = props.tr,
          dispatch = props.dispatch;
      var result = this._results[isNumber(index) ? index : this._activeIndex];

      if (!result) {
        return false;
      }

      if (!dispatch) {
        return true;
      }

      tr.insertText(replacement, result.from, result.to);
      return this.searchNext()(props);
    };
  }

  rebaseNextResult(_ref2) {
    var replacement = _ref2.replacement,
        index = _ref2.index,
        _ref2$lastOffset = _ref2.lastOffset,
        lastOffset = _ref2$lastOffset === void 0 ? 0 : _ref2$lastOffset;
    var nextIndex = index + 1;

    if (!this._results[nextIndex]) {
      return;
    }

    var current = assertGet(this._results, index);
    var offset = current.to - current.from - replacement.length + lastOffset;
    var next = assertGet(this._results, nextIndex);
    this._results[nextIndex] = {
      to: next.to - offset,
      from: next.from - offset
    };
    return offset;
  }

  replaceAll(replacement) {
    return props => {
      var tr = props.tr,
          dispatch = props.dispatch;
      var offset;

      if (isEmptyArray(this._results)) {
        return false;
      }

      if (!dispatch) {
        return true;
      }

      this._results.forEach((_ref3, index) => {
        var from = _ref3.from,
            to = _ref3.to;
        tr.insertText(replacement, from, to);
        offset = this.rebaseNextResult({
          replacement,
          index,
          lastOffset: offset
        });
      });

      return this.find(this._searchTerm)(props);
    };
  }

  find(searchTerm, direction) {
    return _ref4 => {
      var tr = _ref4.tr,
          dispatch = _ref4.dispatch;
      var range = isSelectionEmpty(tr) ? getSelectedWord(tr) : tr.selection;
      var actualSearch = '';

      if (searchTerm) {
        actualSearch = searchTerm;
      } else if (range) {
        var from = range.from,
            to = range.to;
        actualSearch = tr.doc.textBetween(from, to);
      }

      this._searchTerm = this.options.disableRegex ? escapeStringRegexp(actualSearch) : actualSearch;
      this._activeIndex = !direction ? 0 : rotateHighlightedIndex({
        direction,
        previousIndex: this._activeIndex,
        resultsLength: this._results.length
      });
      return this.updateView(tr, dispatch);
    };
  }

  clear() {
    return _ref5 => {
      var tr = _ref5.tr,
          dispatch = _ref5.dispatch;
      this._searchTerm = undefined;
      this._activeIndex = 0;
      return this.updateView(tr, dispatch);
    };
  }
  /**
   * Dispatch an empty transaction to trigger an update of the decoration.
   */


  updateView(tr, dispatch) {
    this._updating = true;

    if (dispatch) {
      dispatch(tr);
    }

    this._updating = false;
    return true;
  }

  createDecoration(doc) {
    this.gatherSearchResults(doc);
    var decorations = this.getDecorations();
    return decorations ? DecorationSet.create(doc, decorations) : [];
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "search", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "search"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "searchNext", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "searchNext"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "searchPrevious", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "searchPrevious"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "replaceSearchResult", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "replaceSearchResult"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "replaceAllSearchResults", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "replaceAllSearchResults"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "clearSearch", [_dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "clearSearch"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "searchForwardShortcut", [_dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "searchForwardShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "searchBackwardShortcut", [_dec9], Object.getOwnPropertyDescriptor(_class2.prototype, "searchBackwardShortcut"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "escapeShortcut", [_dec10], Object.getOwnPropertyDescriptor(_class2.prototype, "escapeShortcut"), _class2.prototype)), _class2)) || _class);
var rotateHighlightedIndex = props => {
  var direction = props.direction,
      resultsLength = props.resultsLength,
      previousIndex = props.previousIndex;

  if (direction === 'next') {
    return previousIndex + 1 > resultsLength - 1 ? 0 : previousIndex + 1;
  }

  return previousIndex - 1 < 0 ? resultsLength - 1 : previousIndex - 1;
};

export { SearchExtension, rotateHighlightedIndex };
